import React, {ReactElement, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {Button, Card, Col, DatePicker, Descriptions, Modal, Row, Table, Tabs, Tag} from "antd"
import {Helmet} from "react-helmet-async"
import {useDispatch, useSelector} from "react-redux"
import {NavLink, useHistory, useLocation, useParams} from "react-router-dom"
import {StateType} from "../../store/store"
import {BackButton} from "../../components/BackButton/BackButton"
import {Loading} from "../../components/Loading/Loading"
import moment from "moment/moment"
import {armingObject, deleteObject, getObject, setObject, setObjectSideFilter, setWSObject} from "../../store/objectReducer"
import {center} from "../../helps/tableHelper"
import {getPSLeakDevices, setPSLeakDevices, setWSPSLeakDevice} from "../../store/psLeakDeviceReducer"
import {isActiveOptionsSelect, limitOptionsSelect, securityChangeModeOptions} from "../../helps/optionsSelect"
import {filterToString} from "../../helps/filterToString"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {AttributeRuleType, AttributeType, UserTypeEnum} from "../../types/types"
import {FormSelect} from "../../components/Form/FormSelect"
import {ObjectSchedules} from "./ObjectSchedule/ObjectSchedules"
import {getObjectSecurityType} from "../../helps/scheduleHelper"
import {PSLeakDeviceResponse, SecurityLogResponse} from "../../models/response"
import {getClassNameLocalLogLinkText} from "../../helps/getClassNameLocalLogLinkText"
import {getSecurityLogs, setSecurityLogs} from "../../store/securityLogReducer"
import {useGetDevices, useGetUsers} from "../../hooks/useFetchHooks"
import {getTimeZoneDiffString} from "../../helps/timezoneDiff"
import {FormInput} from "../../components/Form/FormInput"
import locale from "antd/es/date-picker/locale/ru_RU"
import dayjs from "dayjs"

const columnsDevices = [
    {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: "fit-content",
        align: center,
    },
    {
        title: "Название",
        dataIndex: "name",
        key: "name",
        align: center,
    },
    {
        title: "UIN",
        dataIndex: "uin",
        key: "uin",
        align: center,
    },
    {
        title: "Версия",
        dataIndex: "version",
        key: "version",
        align: center,
    },
    {
        title: "Статус",
        dataIndex: "status",
        key: "status",
        render: (psLeakDevice: PSLeakDeviceResponse) =>
        {
            const problems: ReactElement[] = [<Tag key="active" color={psLeakDevice.active ? "green" : "volcano"} style={{marginRight: 0}}>{psLeakDevice.active ? "Активно" : "Не активно"}</Tag>]

            if (psLeakDevice.active && psLeakDevice.offline)
            {
                problems.push(<Tag key="offline" color="volcano" style={{marginRight: 0}}>Не выходит на связь</Tag>)
            }

            if (psLeakDevice.lowMemoryNotification)
            {
                problems.push(<Tag key="lowMemoryNotification" color="gold" style={{marginRight: 0}}>Заканчивается память устройства</Tag>)
            }

            if (psLeakDevice.disableLocalData)
            {
                problems.push(<Tag key="disableLocalData" color="gold" style={{marginRight: 0}}>Не принимаются локальные данные</Tag>)
            }

            if (psLeakDevice.memoryError)
            {
                problems.push(<Tag key="disableLocalData" color="gold" style={{marginRight: 0}}>Проблемы с записью в память</Tag>)
            }

            if (psLeakDevice.idZeroError)
            {
                problems.push(<Tag key="disableLocalData" color="gold" style={{marginRight: 0}}>Устройство присылает ID 0 {moment(psLeakDevice.lastDateIdZero).format("DD.MM.YYYY HH:mm")}</Tag>)
            }

            if (psLeakDevice.versionLocalNeedUpdate)
            {
                problems.push(<Tag key="versionLocalNeedUpdate" color="gold" style={{marginRight: 0}}>Требуется обновление версии</Tag>)
            }

            if (psLeakDevice.attributesServerDeprecated || psLeakDevice.attributesLocalDeprecated)
            {
                problems.push(<Tag key="attributesDeprecated" color="gold" style={{marginRight: 0}}>Атрибуты устарели</Tag>)
            }

            if (psLeakDevice.currentStatus?.length > 0)
            {
                psLeakDevice?.currentStatus?.forEach(i => problems.push(
                    <div key={`local_log_${i.id}`}>
                        <NavLink style={{fontSize: 13}}
                                 className={getClassNameLocalLogLinkText(i.idLogType)}
                                 to={`/psLeakDeviceLocalLog/${i.id}`}
                                 onClick={(e) => e.stopPropagation()}>
                            {i.description}
                        </NavLink>
                    </div>,
                ))
            }

            return (
                <div className="table-cell-status">
                    {problems}
                </div>
            )
        },
        align: center,
    },
]

const columnsSecurityLogs = [
    {
        title: "Дата",
        dataIndex: "date",
        key: "date",
        align: center,
    },
    {
        title: "Объект",
        dataIndex: "nameObject",
        key: "nameObject",
        align: center,
    },
    {
        title: "Оборудование",
        dataIndex: "device",
        key: "device",
        align: center,
        render: (device?: { id: number, name: string } | null) =>
        {
            if (!device) return null
            return <NavLink to={`/psLeakDevice/${device.id}`}>{device.name}</NavLink>
        },
    },
    {
        title: "Сообщение",
        dataIndex: "action",
        key: "action",
        align: center,
    },
    {
        title: "Режим",
        dataIndex: "changeModeUser",
        key: "changeModeUser",
        align: center,
        render: (changeModeUser: { changeMode: SecurityLogResponse["changeMode"], user?: { id: number, name: string, email: string } }) =>
        {
            const {changeMode, user} = changeModeUser
            switch (changeMode)
            {
                case "MANUAL":
                    return `Ручной${user ? ` (${user.name} - ${user.email})` : ""}`
                case "SCHEDULE":
                    return "Расписание"
                case "DEVICE":
                    return "Устройство"
                default:
                    return changeMode
            }
        },
    },
]

export const Object = () =>
{
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const id = parseInt(useParams<{ id: string }>()?.id, 10)
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const status = useSelector((state: StateType) => state.appReducer.status)
    const object = useSelector((state: StateType) => state.objectReducer.object)
    const filter = useSelector((state: StateType) => state.objectReducer.sideFilter)
    const devices = useSelector((state: StateType) => state.psLeakDeviceReducer.psLeakDevices)
    const securityLogs = useSelector((state: StateType) => state.securityLogReducer.securityLogs)
    const ws = useSelector((state: StateType) => state.objectReducer.ws)
    const wsDevice = useSelector((state: StateType) => state.psLeakDeviceReducer.ws)
    const wsSecurityLogs = useSelector((state: StateType) => state.securityLogReducer.ws)
    const [tableSize, setTableSize] = useState(0)
    const ref = useRef<HTMLDivElement>(null)
    const [showModal, setShowModal] = useState(false)
    const [showModalSchedules, setShowModalSchedules] = useState(false)
    const [loading, setLoading] = useState(false)
    const controller = useMemo(() => new AbortController(), [id])

    useEffect(() =>
    {
        if (status !== "loading") setTableSize(0)
    }, [status])

    const devicesQuery = useMemo(() => ({
        idCompany: object?.idCompany,
        idObject: object?.id,
    }), [object])
    const devicesOpt = useGetDevices(devicesQuery)

    const usersQuery = useMemo(() => ({
        idCompany: object?.idCompany,
    }), [object])
    const users = useGetUsers(usersQuery)

    useEffect(() =>
    {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter))
        {
            dispatch(setObjectSideFilter({...filterString}))
        }
        else if (location.search !== filterToString(filter))
        {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    useEffect(() =>
    {
        if (id) dispatch(getObject({id, controller}))

        return () =>
        {
            controller.abort()
            dispatch(setObject(null))
        }
    }, [dispatch, id, controller])

    const getPSLeakDevicesFunc = useCallback((withoutLoadingIndicator = false) =>
    {
        if (id && location?.search)
        {
            const filter = filterFromString(location.search)
            if (filter?.tabKey === "1")
            {
                if (filter.limitDevices) filter.limit = filter.limitDevices
                if (filter.offsetDevices) filter.offset = filter.offsetDevices
                if (filter.searchDevices) filter.search = filter.searchDevices
                dispatch(getPSLeakDevices({idObject: id, ...filter, controller, withoutLoadingIndicator}))
            }
        }
    }, [dispatch, id, location, controller])

    const getSecurityLogsFunc = useCallback((withoutLoadingIndicator = false) =>
    {
        if (id && location?.search)
        {
            const filter = filterFromString(location.search)
            if (filter?.tabKey === "2")
            {
                if (filter.limitSecurityLogs) filter.limit = filter.limitSecurityLogs
                if (filter.offsetSecurityLogs) filter.offset = filter.offsetSecurityLogs
                if (filter.dateFromSecurityLogs) filter.dateFrom = filter.dateFromSecurityLogs
                if (filter.dateToSecurityLogs) filter.dateTo = filter.dateToSecurityLogs
                if (filter.searchSecurityLogs) filter.search = filter.searchSecurityLogs
                dispatch(getSecurityLogs({idObject: id, ...filter, controller, withoutLoadingIndicator}))
            }
        }
    }, [dispatch, id, location, controller])

    useEffect(() =>
    {
        getPSLeakDevicesFunc()
        getSecurityLogsFunc()

        return () =>
        {
            dispatch(setPSLeakDevices(null))
            dispatch(setSecurityLogs(null))
        }
    }, [dispatch, getPSLeakDevicesFunc, getSecurityLogsFunc])

    const deleteHandler = useCallback(async () =>
    {
        if (object?.id)
        {
            const {payload}: any = await dispatch(deleteObject(object.id))
            if (payload?.status === 0)
            {
                setShowModal(false)
                if (payload?.message === "Удалено") history.goBack()
                else dispatch(getObject({id, controller}))
            }
        }
    }, [object, history, id, controller])

    useEffect(() =>
    {
        if (ws && ws.id === id)
        {
            dispatch(getObject({id, controller, withoutLoadingIndicator: true}))
        }
        dispatch(setWSObject(null))
    }, [dispatch, ws, id, controller])

    useEffect(() =>
    {
        if (wsDevice && wsDevice.idParent === id)
        {
            getPSLeakDevicesFunc(true)
        }
        dispatch(setWSPSLeakDevice(null))
    }, [dispatch, id, wsDevice, getPSLeakDevicesFunc])

    useEffect(() =>
    {
        if (wsSecurityLogs && wsSecurityLogs.idParent === id)
        {
            getSecurityLogsFunc(true)
        }
        dispatch(setSecurityLogs(null))
    }, [dispatch, id, wsSecurityLogs, getSecurityLogsFunc])

    const arming = useCallback(async () =>
    {
        if (object)
        {
            try
            {
                setLoading(true)
                await dispatch(armingObject({
                    id: object.id,
                    securityActive: !object.securityActive,
                    controller,
                }))
            } finally
            {
                setLoading(false)
            }
        }
    }, [object])

    const description = useMemo(() =>
    {
        if (object)
        {
            const parsedAttributes: Array<AttributeType> = JSON.parse(object.attributes)
            const parsedRules: Array<AttributeRuleType> = JSON.parse(object.attributesRules)

            const map = [
                <Descriptions.Item key="name" label="Название">{object?.name}</Descriptions.Item>,
                <Descriptions.Item key="id" label="ID">{object?.id}</Descriptions.Item>,
                <Descriptions.Item key="nameCompany" label="Компания">
                    <NavLink to={`/company/${object.idCompany}`}>{object.nameCompany}</NavLink>
                </Descriptions.Item>,
                <Descriptions.Item key="nameObjectGroup" label="Группа">
                    <NavLink to={`/objectGroup/${object.idObjectGroup}`}>{object.nameObjectGroup}</NavLink>
                </Descriptions.Item>,
                <Descriptions.Item key="createdAt" label="Создана">{moment(object?.createdAt).format("DD.MM.YYYY HH:mm")}</Descriptions.Item>,
                <Descriptions.Item key="updatedAt" label="Обновлена">{moment(object?.updatedAt).format("DD.MM.YYYY HH:mm")}</Descriptions.Item>,
                <Descriptions.Item key="address" label="Адрес">{object?.address || ""}</Descriptions.Item>,
                <Descriptions.Item key="active" label="Статус">
                    <div className="description-item-status">
                        <Tag color={object.active ? "green" : "volcano"} style={{marginRight: 0}}>{object.active ? "Активно" : "Не активно"}</Tag>
                        {object?.deleted ? <Tag color="volcano" style={{marginRight: 0}}>Удалено</Tag> : null}
                    </div>
                </Descriptions.Item>,
                <Descriptions.Item key="securityActive" label="Статус охраны">
                    <div className="description-item-status">
                        <Tag color={object.securityActive ? "green" : "volcano"} style={{marginRight: 0}}>{object.securityActive ? "Активно" : "Не активно"}</Tag>
                        {
                            object.active
                                ? <Button onClick={arming} type="link" size="small" style={{padding: 0}} loading={loading}>
                                    <span style={{color: object.securityActive ? "#ff4949" : "#2bbb00"}}>{object.securityActive ? "Снять с охраны" : "На охрану"}</span>
                                </Button>
                                : null
                        }
                    </div>
                </Descriptions.Item>,
                <Descriptions.Item key="securityType" label="Способ постановки на охрану">
                    <Tag color="default" style={{marginRight: 0}}>{getObjectSecurityType(object.securityType)}</Tag>
                </Descriptions.Item>,
                <Descriptions.Item key="description" label="Описание">{object.description}</Descriptions.Item>,
            ]

            if (Array.isArray(parsedRules) && Array.isArray(parsedAttributes))
            {
                parsedRules.forEach(i =>
                {
                    const attr = parsedAttributes.find((a: any) => a.alias === i.alias)

                    if (attr)
                    {
                        let value = null

                        if (Array.isArray(attr.value))
                        {
                            value = attr.value.map((option: string) =>
                            {
                                const opt = i.options?.find((ruleOption) => ruleOption.value === option)
                                if (opt) return <div key={opt.value}>{opt.label}</div>
                            })
                        }
                        else if (attr.value?.includes("option_"))
                        {
                            const opt = i.options?.find((ruleOption) => ruleOption.value === attr.value)
                            if (opt) value = <div key={opt.value}>{opt.label}</div>
                        }
                        else value = <div>{attr.value}</div>

                        map.push(<Descriptions.Item key={i.alias} label={i.name}>{value}</Descriptions.Item>)
                    }
                })
            }

            return (
                <Descriptions bordered column={{xs: 1, sm: 1, md: 2}}>
                    {map}
                </Descriptions>
            )
        }
    }, [object, loading])

    const items = useMemo(() => [
        {
            key: "1",
            label: "Устройства",
        },
        {
            key: "2",
            label: "Логи охраны",
        },

    ], [object])

    const tableContent = useMemo(() =>
    {
        switch (filter?.tabKey)
        {
            case "1":
            {
                const dataDevice = devices?.data?.map(i =>
                {
                    return {
                        key: i.id,
                        id: i.id,
                        name: i.name,
                        uin: i.uin,
                        version: i.versionLocal,
                        status: i,
                    }
                })

                return <div className="table-responsive" ref={ref} style={{minHeight: tableSize}}>
                    {
                        devices
                            ? <Table columns={columnsDevices}
                                     dataSource={dataDevice}
                                     pagination={filter?.limitDevices ? {
                                         position: ["bottomLeft", "topLeft"],
                                         pageSize: filter.limitDevices,
                                         hideOnSinglePage: true,
                                         total: devices.total,
                                         current: filter?.offsetDevices / filter?.limitDevices + 1,
                                         onChange: (page, pageSize) =>
                                         {
                                             setTableSize(ref?.current?.offsetHeight || 0)
                                             const obj = {
                                                 limitDevices: pageSize,
                                                 offsetDevices: (page - 1) * pageSize,
                                             }
                                             history.replace({search: filterToString({...filter, ...obj})})
                                         },
                                         showSizeChanger: false,
                                     } : false}
                                     className="ant-border-space"
                                     onRow={({id}) => ({
                                         onClick: () => history.push(`/psLeakDevice/${id}`),
                                         onMouseDown: (e) => e.button === 1 && window.open(`/psLeakDevice/${id}`, "_blank", "noopener,noreferrer"),
                                     })}
                                     rowClassName={({status}) => status.deleted || !status.active
                                         ? "table-row-deleted"
                                         : status.active && status.offline
                                             ? "table-row-danger"
                                             : ""}
                            />
                            : <Loading/>
                    }
                </div>
            }
            case "2":
            {
                const dataSecurityLogs = securityLogs?.data?.map(i =>
                {
                    return {
                        key: i.id,
                        id: i.id,
                        action: i.action,
                        type: i.type,
                        nameObject: i.nameObject,
                        device: i.idDevice ? {id: i.idDevice, name: i.nameDevice} : null,
                        date: `${moment(i.date).format("DD.MM.YYYY HH:mm")} ${getTimeZoneDiffString(i.timeZone)}`,
                        changeModeUser: {changeMode: i.changeMode, user: i.idUser ? {id: i.idUser, name: i.nameUser, email: i.emailUser} : null},
                    }
                })

                return <div className="table-responsive" ref={ref} style={{minHeight: tableSize}}>
                    {
                        securityLogs
                            ? <Table columns={columnsSecurityLogs}
                                     dataSource={dataSecurityLogs}
                                     pagination={filter?.limitSecurityLogs ? {
                                         position: ["bottomLeft", "topLeft"],
                                         pageSize: filter.limitSecurityLogs,
                                         hideOnSinglePage: true,
                                         total: securityLogs.total,
                                         current: filter?.offsetSecurityLogs / filter?.limitSecurityLogs + 1,
                                         onChange: (page, pageSize) =>
                                         {
                                             setTableSize(ref?.current?.offsetHeight || 0)
                                             const obj = {
                                                 limitSecurityLogs: pageSize,
                                                 offsetSecurityLogs: (page - 1) * pageSize,
                                             }
                                             history.replace({search: filterToString({...filter, ...obj})})
                                         },
                                         showSizeChanger: false,
                                     } : false}
                                     className="ant-border-space"
                            />
                            : <Loading/>
                    }
                </div>
            }
        }
    }, [devices, securityLogs, history, filter, tableSize])

    const deviceOptions = useMemo(() => devicesOpt?.data?.map(i => ({label: i.name, value: i.id})), [devicesOpt])
    const userOptions = useMemo(() => users?.data?.map(i => ({label: `${i.name} - ${i.email}`, value: i.id})), [users])

    if (!object) return <Loading/>

    return (
        <div className="description-page">
            <Helmet>
                <title>{object?.name}</title>
            </Helmet>
            <div className="description-header">
                <BackButton onClick={() => history.goBack()}/>
                <div>
                    <Button type="link" onClick={() => setShowModalSchedules(true)}>
                        {object.hasAnySchedule ? "Изменить расписание" : "Установить расписание"}
                    </Button>
                    {!object?.deleted ? <Button type="link" onClick={() => history.push(`/objectForm`, {object})}>Редактировать</Button> : null}
                    <Button type="link" danger onClick={() => setShowModal(true)}>Удалить</Button>
                </div>
            </div>

            {description}

            <div className="tabled-side">
                <Row gutter={[24, 0]}>
                    <Col xs={24}>
                        <Card variant="borderless"
                              className="criclebox tablespace mb-24"
                              title={
                                  <Tabs defaultActiveKey="1"
                                        activeKey={filter?.tabKey}
                                        items={items}
                                        size="large"
                                        onTabClick={(key) =>
                                        {
                                            setTableSize(ref?.current?.offsetHeight || 0)
                                            history.replace({search: filterToString({...filter, tabKey: key})})
                                            if (id && filter)
                                            {
                                                if (filter.tabKey === key && filter.tabKey === "1")
                                                {
                                                    dispatch(getPSLeakDevices({idObject: object.id, ...filterFromString(location.search), controller}))
                                                }
                                                if (filter.tabKey === key && filter.tabKey === "2")
                                                {
                                                    dispatch(getSecurityLogs({idObject: object.id, ...filterFromString(location.search), controller}))
                                                }
                                            }
                                        }}
                                  />
                              }
                              extra={
                                  <div className="table-title-extra">
                                      {
                                          filter?.tabKey === "1"
                                              ? <>
                                                  <Button onClick={() => history.push("/psLeakDeviceForm",
                                                      {idCompany: object?.idCompany, idObjectGroup: object?.idObjectGroup, idObject: id})}
                                                          type="link" size="middle" style={{marginTop: 5}}>
                                                      Добавить
                                                  </Button>

                                                  <FormInput placeholder="Поиск"
                                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                             value={filter?.searchDevices}
                                                             name="search"
                                                             onChange={(e) => history.replace({search: filterToString({...filter, searchDevices: e.target.value, offsetDevices: 0})})}
                                                             allowClear
                                                  />

                                                  <FormSelect value={filter?.status}
                                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                              dropdownStyle={{minWidth: "min-content"}}
                                                              onChange={(value) =>
                                                              {
                                                                  setTableSize(ref?.current?.offsetHeight || 0)
                                                                  history.replace({search: filterToString({...filter, status: value, offsetDevices: 0})})
                                                              }}
                                                              options={[...isActiveOptionsSelect, {value: "2", label: "Требуют внимания"}]}
                                                              placeholder="Статус"
                                                              allowClear
                                                              placement="bottomRight"
                                                  />

                                                  <FormSelect value={filter?.limitDevices}
                                                              containerStyle={{marginBottom: 0, width: "auto"}}
                                                              dropdownStyle={{minWidth: "min-content"}}
                                                              onChange={(value) =>
                                                              {
                                                                  setTableSize(ref?.current?.offsetHeight || 0)
                                                                  history.replace({search: filterToString({...filter, limitDevices: value, offsetDevices: 0})})
                                                              }}
                                                              options={limitOptionsSelect}
                                                              placement="bottomRight"
                                                  />
                                              </>
                                              : null
                                      }

                                      {
                                          filter?.tabKey === "2"
                                              ? <>
                                                  <FormInput placeholder="Поиск"
                                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                             value={filter?.searchSecurityLogs}
                                                             name="search"
                                                             onChange={(e) => history.replace({search: filterToString({...filter, searchSecurityLogs: e.target.value, offsetSecurityLogs: 0})})}
                                                             allowClear
                                                  />

                                                  <DatePicker locale={locale}
                                                              showTime
                                                              placeholder="Дата от"
                                                              format="DD.MM.YYYY HH:mm"
                                                              showSecond={false}
                                                              style={{height: 50, width: 150}}
                                                              value={filter.dateFromSecurityLogs ? dayjs(filter.dateFromSecurityLogs) : null}
                                                              onChange={(value) => history.replace({search: filterToString({...filter, dateFromSecurityLogs: value ? value.format("YYYY-MM-DD HH:mm") : null, offsetSecurityLogs: 0})})}
                                                              disabledDate={(currentDate) => currentDate > dayjs(new Date())}
                                                  />

                                                  <DatePicker locale={locale}
                                                              showTime
                                                              placeholder="Дата по"
                                                              format="DD.MM.YYYY HH:mm"
                                                              showSecond={false}
                                                              style={{height: 50, width: 150}}
                                                              value={filter.dateToSecurityLogs ? dayjs(filter.dateToSecurityLogs) : null}
                                                              onChange={(value) => history.replace({search: filterToString({...filter, dateToSecurityLogs: value ? value.format("YYYY-MM-DD HH:mm") : null, offsetSecurityLogs: 0})})}
                                                              disabledDate={(currentDate) => filter.dateFromSecurityLogs ? currentDate < dayjs(filter.dateFromSecurityLogs) || currentDate > dayjs(new Date()) : currentDate > dayjs(new Date())}
                                                  />

                                                  <FormSelect value={filter?.idDevice}
                                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                              dropdownStyle={{minWidth: "min-content"}}
                                                              onChange={(value) =>
                                                              {
                                                                  setTableSize(ref?.current?.offsetHeight || 0)
                                                                  history.replace({search: filterToString({...filter, idDevice: value, offsetSecurityLogs: 0})})
                                                              }}
                                                              options={deviceOptions}
                                                              placeholder="Устройство"
                                                              allowClear
                                                              placement="bottomRight"
                                                  />

                                                  {
                                                      profile?.idUserType === UserTypeEnum.ADMIN_SYSTEM || profile?.idUserType === UserTypeEnum.ADMIN_SYSTEM
                                                          ? <FormSelect value={filter?.idUser}
                                                                        containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                                        dropdownStyle={{minWidth: "min-content"}}
                                                                        onChange={(value) =>
                                                                        {
                                                                            setTableSize(ref?.current?.offsetHeight || 0)
                                                                            history.replace({search: filterToString({...filter, idUser: value, offsetSecurityLogs: 0})})
                                                                        }}
                                                                        options={userOptions}
                                                                        placeholder="Пользователь"
                                                                        allowClear
                                                                        placement="bottomRight"
                                                          />
                                                          : null
                                                  }

                                                  <FormSelect value={filter?.changeMode}
                                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                              dropdownStyle={{minWidth: "min-content"}}
                                                              onChange={(value) => history.replace({search: filterToString({...filter, changeMode: value, offsetSecurityLogs: 0})})}
                                                              options={securityChangeModeOptions}
                                                              placeholder="Режим"
                                                              allowClear
                                                              placement="bottomRight"
                                                  />

                                                  <FormSelect value={filter?.limitSecurityLogs}
                                                              containerStyle={{marginBottom: 0, width: "auto"}}
                                                              dropdownStyle={{minWidth: "min-content"}}
                                                              onChange={(value) =>
                                                              {
                                                                  setTableSize(ref?.current?.offsetHeight || 0)
                                                                  history.replace({search: filterToString({...filter, limitSecurityLogs: value, offsetSecurityLogs: 0})})
                                                              }}
                                                              options={limitOptionsSelect}
                                                              placement="bottomRight"
                                                  />
                                              </>
                                              : null
                                      }
                                  </div>
                              }
                        >
                            {tableContent}
                        </Card>
                    </Col>
                </Row>
            </div>

            {
                showModalSchedules
                    ? <ObjectSchedules idObject={object.id}
                                       dispatch={dispatch}
                                       showModalSchedules={showModalSchedules}
                                       setShowModalSchedules={setShowModalSchedules}
                    />
                    : null
            }

            <Modal title={`Вы уверены что хотите удалить объект`}
                   centered
                   open={showModal}
                   onOk={deleteHandler}
                   onCancel={() => setShowModal(false)}
                   okText="Да"
                   okButtonProps={{loading: status === "loading"}}
            />
        </div>
    )
}